import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { settingsService, getInstanceId } from '../lib/settings';
import { adminFetch, adminSession, adminUpload } from '../lib/extras';
import { authService } from '../lib/auth';
import TwoFactorModal from './TwoFactorModal';
import type { PriceBand } from '../lib/preCartCheck';

// Nastavení a přehledy převzaté z GEP/Design. Zobrazuje se v záložkách administrace:
//  section="ai"       → AI (modely a jejich napojení, statistiky, náklady, kurz)
//  section="cart"     → Tisk a arch (kontrola před košíkem, orientační ceník)
//  section="print"    → Tisk a arch (Tisk a barvy: ICC profil CMYK, převodní záměr, ořezová kontura)
//  section="online"   → Klienti online (živé počty, návštěvy, odkud přišli)
//  section="users"    → Uživatelé (role, poslední přihlášení, 2FA)
//  section="security" → Zabezpečení (souhrn přihlášení, 2FA, upozornění)
//  section="health"   → Provoz (stav serveru a napojení)
type Section = 'ai' | 'cart' | 'print' | 'online' | 'users' | 'security' | 'health' | 'people';
interface Props { section: Section; isSuper: boolean; email?: string }

type Gen = Record<string, any>;

const h4 = 'text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] border-b-2 border-gray-50 pb-2';
const box = 'p-4 bg-gray-50 border-2 border-gray-300 rounded-md';
const label = 'text-[10px] font-black text-gray-700 uppercase tracking-widest block';
const hint = 'text-[9px] font-bold text-gray-400 block mt-1';
const num = 'w-24 px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-sm font-bold text-center shadow-inner';
const txt = 'w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-xs font-bold outline-none';
const btn = 'px-4 py-2 bg-white border-2 border-gray-300 rounded-md text-[10px] font-black uppercase tracking-widest hover:border-gray-500 disabled:opacity-50';
const btnRed = 'px-4 py-2 bg-red-600 text-white rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-red-700 disabled:opacity-50';

function useGeneral(): [Gen, (patch: Gen) => Promise<void>] {
  const [g, setG] = useState<Gen>({});
  useEffect(() => {
    if (!db) return;
    getDoc(doc(db, 'settings', getInstanceId())).then(s => setG(s.data() || {})).catch(() => {});
  }, []);
  const save = async (patch: Gen) => {
    setG(prev => ({ ...prev, ...patch }));
    await settingsService.saveGeneralSettings(patch as any);
    if (['priceTable', 'priceCurrency', 'priceNote', 'preCartCheck', 'minPrintDpi'].some(k => k in patch)) {
      window.dispatchEvent(new CustomEvent('rd-cart-settings', { detail: patch }));
    }
  };
  return [g, save];
}

const Toggle: React.FC<{ title: string; desc?: string; checked: boolean; onChange: (v: boolean) => void }> = ({ title, desc, checked, onChange }) => (
  <label className={`${box} flex items-center justify-between gap-4 cursor-pointer hover:border-blue-500 transition-all`}>
    <span><span className={label}>{title}</span>{desc && <span className={hint}>{desc}</span>}</span>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="h-5 w-5 rounded-md focus:ring-0 shrink-0" />
  </label>
);

// ---------------- AI ----------------
const fmtKc = (usd: number, rate: number) => `${Math.round(usd * rate).toLocaleString('cs-CZ')} Kč`;
const fmtDate = (iso?: string | null) => iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const ago = (iso?: string | null) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 1) return 'právě teď'; if (m < 60) return `před ${m} min`;
  const h = Math.round(m / 60); if (h < 48) return `před ${h} h`;
  return `před ${Math.round(h / 24)} dny`;
};
const Stat: React.FC<{ t: string; v: React.ReactNode; s?: React.ReactNode }> = ({ t, v, s }) => (
  <div className={`${box} text-center`}>
    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest block">{t}</span>
    <span className="text-2xl font-black text-gray-900 block">{v}</span>
    {s !== undefined && <span className="text-[10px] text-gray-500">{s}</span>}
  </div>
);
const Err: React.FC<{ m: string }> = ({ m }) => <div className="p-2 text-xs font-bold bg-red-50 text-red-700 border-2 border-red-100 rounded-md">{m}</div>;
const Note: React.FC<{ m: string }> = ({ m }) => <div className="p-2 text-xs font-bold bg-amber-50 text-amber-800 border-2 border-amber-100 rounded-md">{m}</div>;

type Range = 'today' | 'd7' | 'd30';
const RANGE_NAMES: Record<Range, string> = { today: 'Dnes', d7: '7 dní', d30: '30 dní' };

const AiSection: React.FC<{ isSuper: boolean }> = ({ isSuper }) => {
  const [g, save] = useGeneral();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [range, setRange] = useState<Range>('d30');
  const [tests, setTests] = useState<Record<string, any>>({});
  const [keyIn, setKeyIn] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const load = () => adminFetch('/api/admin/ai-models').then(r => { setD(r); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);
  const rate = g.czkPerUsd ?? d?.czkPerUsd ?? 23;

  const saveCfg = async (patch: any) => {
    setBusy(true); setMsg(null);
    try { await adminFetch('/api/admin/ai-models', { body: patch }); setMsg('Uloženo. Platí pro další generování.'); await load(); }
    catch (e: any) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const saveKey = async (provider: string, key: string) => {
    setBusy(true); setMsg(null);
    try { await adminFetch('/api/admin/ai-keys', { body: { provider, key } }); setKeyIn(k => ({ ...k, [provider]: '' })); setMsg(key ? 'Klíč uložen (šifrovaně, jen na serveru).' : 'Klíč odebrán.'); await load(); }
    catch (e: any) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const test = async (model: string) => {
    setTests(t => ({ ...t, [model]: { busy: true } }));
    try { const r = await adminFetch('/api/admin/ai-test', { body: { model } }); setTests(t => ({ ...t, [model]: r })); }
    catch (e: any) { setTests(t => ({ ...t, [model]: { ok: false, detail: e.message } })); }
  };

  if (err) return <Err m={err} />;
  if (!d) return <div className={box}><span className={hint}>Načítám AI modely…</span></div>;
  const canEdit = isSuper && d.canEdit;
  const t = d.totals;
  const ready = d.models.filter((m: any) => m.ready);
  const modelSelect = (field: 'primary' | 'second' | 'fallback', title: string, desc: string, allowOff: boolean) => (
    <div className={`${box} flex items-center justify-between gap-3 flex-wrap`}>
      <div className="min-w-[200px] flex-1"><span className={label}>{title}</span><span className={hint}>{desc}</span></div>
      <select disabled={!canEdit || busy} value={d.config[field] ?? ''} onChange={e => saveCfg({ [field]: e.target.value })}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-xs font-bold max-w-full">
        {allowOff && <option value="">{field === 'second' ? 'Vypnuto (jen 2 návrhy)' : 'Bez zálohy'}</option>}
        {d.models.map((m: any) => <option key={m.key} value={m.key} disabled={!m.ready}>{m.label} · {m.providerLabel}{m.ready ? '' : ' (nepřipojeno)'}</option>)}
      </select>
    </div>
  );
  const usedNames = Object.fromEntries(d.models.map((m: any) => [m.key, m.label]));
  const activeKeys = d.models.filter((m: any) => m.stats.d30.calls > 0).map((m: any) => m.key);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h5 className="rda-h5">Přehled AI</h5>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat t="Zákazníci s AI dnes" v={t.today.users} s={`${t.d7.users} za 7 dní · ${t.d30.users} za 30 dní`} />
          <Stat t="Požadavky dnes" v={t.today.requests} s={`${t.d30.requests} za 30 dní`} />
          <Stat t="Obrázky dnes" v={t.today.images} s={`strop ${d.limits.usedToday} / ${d.limits.global}`} />
          <Stat t="Náklady dnes" v={fmtKc(t.today.costUsd, rate)} s={`${fmtKc(t.d30.costUsd, rate)} za 30 dní`} />
        </div>
        {(t.d30.adminImages > 0) && <p className="text-[10px] text-gray-500">Z toho administrátoři za 30 dní: {t.d30.adminImages} obrázků za {fmtKc(t.d30.adminCostUsd, rate)} (do počtu zákazníků se nepočítají).</p>}
      </div>

      <div className="space-y-3">
        <h5 className="rda-h5">Napojení poskytovatelů</h5>
        {msg && <Note m={msg} />}
        {d.providers.map((p: any) => (
          <div key={p.key} className={`${box} space-y-2`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className={label}>{p.label}</span>
                <span className={hint}>{p.ok ? `Připojeno · ${p.source}${p.tail ? ` · klíč …${p.tail}` : ''}` : `Nepřipojeno · ${p.source}`}</span>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest ${p.ok ? 'text-green-700' : 'text-gray-400'}`}>{p.ok ? 'Připojeno' : 'Nepřipojeno'}</span>
            </div>
            {p.needsKey && canEdit && p.source !== `proměnná ${p.keyEnv}` && (
              <div className="flex gap-2 flex-wrap">
                <input type="password" autoComplete="off" className={`${txt} flex-1 min-w-[200px] font-mono`} placeholder={p.ok ? 'Nový klíč (nahradí uložený)' : 'Vložte API klíč'}
                  value={keyIn[p.key] || ''} onChange={e => setKeyIn(k => ({ ...k, [p.key]: e.target.value }))} />
                <button className={btn} disabled={busy || !(keyIn[p.key] || '').trim()} onClick={() => saveKey(p.key, (keyIn[p.key] || '').trim())}>Uložit klíč</button>
                {p.ok && p.source === 'uloženo v administraci' && <button className={btn} disabled={busy} onClick={() => window.confirm('Odebrat uložený klíč? Modely tohoto poskytovatele přestanou fungovat.') && saveKey(p.key, '')}>Odebrat</button>}
              </div>
            )}
            {p.needsKey && p.source === `proměnná ${p.keyEnv}` && <span className={hint}>Klíč je nastavený na serveru (Cloud Run), v administraci ho nelze měnit.</span>}
          </div>
        ))}
        {!isSuper && <p className="text-[10px] text-gray-500">Klíče a výběr modelů může měnit jen superadministrátor.</p>}
      </div>

      <div className="space-y-3">
        <h5 className="rda-h5">Které modely kreslí návrhy</h5>
        {modelSelect('primary', 'Hlavní model – návrhy 1 a 2', 'Používá se vždy.', false)}
        {modelSelect('second', 'Druhý model – návrhy 3 a 4', 'Jiný model = jiný styl a vlastní kvóta. Vypnuto = zákazník dostane jen 2 návrhy.', true)}
        {modelSelect('fallback', 'Záložní model', 'Když hlavní nebo druhý model selže (vyčerpaná kvóta, výpadek), návrh se jednou zkusí tímto modelem.', true)}
        {d.config.updatedBy && <p className="text-[10px] text-gray-500">Naposledy změnil {d.config.updatedBy} · {fmtDate(new Date(d.config.updatedAt).toISOString())}</p>}
        {ready.length === 0 && <Err m="Žádný poskytovatel AI není připojený." />}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h5 className="rda-h5">Statistiky modelů</h5>
          <div className="flex gap-1">
            {(Object.keys(RANGE_NAMES) as Range[]).map(r => <button key={r} type="button" className={`${btn} ${range === r ? 'border-gray-900' : ''}`} onClick={() => setRange(r)}>{RANGE_NAMES[r]}</button>)}
          </div>
        </div>
        <div className={`${box} overflow-x-auto`}>
          <table className="w-full text-[11px] min-w-[720px]">
            <thead><tr className="text-left text-gray-500">
              <th className="py-1">Model</th><th>Stav</th><th>Zákazníci</th><th>Obrázky</th><th>Chyby</th><th>Úspěšnost</th><th>Prům. čas</th><th>Náklady</th><th>Cena / obr.</th><th></th>
            </tr></thead>
            <tbody>
              {d.models.map((m: any) => {
                const st = m.stats[range];
                const ok = st.calls ? Math.round(st.images / st.calls * 100) : null;
                const role = [d.config.primary === m.key && 'hlavní', d.config.second === m.key && 'druhý', d.config.fallback === m.key && 'záložní'].filter(Boolean).join(', ');
                const tr = tests[m.key];
                return (
                  <tr key={m.key} className="border-t border-gray-200 align-top">
                    <td className="py-1.5 pr-2"><span className="font-bold block">{m.label}</span><span className="text-gray-500">{m.providerLabel}{role ? ` · ${role}` : ''}</span>
                      {tr && !tr.busy && <span className={`block ${tr.ok ? 'text-green-700' : 'text-red-700'} font-bold`}>{tr.ok ? 'OK' : 'Chyba'}: {tr.detail}{tr.ms ? ` (${tr.ms} ms)` : ''}</span>}
                    </td>
                    <td className={m.ready ? 'text-green-700 font-bold' : 'text-gray-400'}>{m.ready ? 'připojeno' : 'chybí klíč'}</td>
                    <td>{st.users}</td>
                    <td>{st.images}{st.adminImages ? <span className="text-gray-400"> ({st.adminImages} adm.)</span> : null}{st.fallbacks ? <span className="block text-gray-400">{st.fallbacks}× jako záloha</span> : null}</td>
                    <td className={st.fails ? 'text-red-700 font-bold' : ''}>{st.fails}</td>
                    <td>{ok === null ? '—' : `${ok} %`}</td>
                    <td>{st.calls ? `${(st.ms / st.calls / 1000).toFixed(1)} s` : '—'}</td>
                    <td>{fmtKc(st.costUsd, rate)}</td>
                    <td>{canEdit
                      ? <input type="number" min={0} max={5} step={0.001} defaultValue={m.price} title={`Ceník: ${m.defaultPrice} USD`} className="w-20 px-2 py-1 bg-white border-2 border-gray-300 rounded text-[11px] font-bold"
                          onBlur={e => { const v = e.target.value; if (Number(v) !== m.price) saveCfg({ prices: { [m.key]: v === '' ? null : Number(v) } }); }} />
                      : `${m.price} USD`}</td>
                    <td className="text-right"><button type="button" className="text-blue-700 underline text-[10px] font-bold whitespace-nowrap" disabled={tr?.busy || !m.ready} onClick={() => test(m.key)}>{tr?.busy ? 'Zkouším…' : 'Otestovat'}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[9px] text-gray-400 mt-2">Zákazníci = různé anonymní relace prohlížeče za den (bez cookies a osobních údajů; za 7 a 30 dní jde o součet denních počtů), administrátoři se nepočítají. „Otestovat“ ověří klíč a dostupnost modelu bez placeného generování. Ceny jsou ceníkové odhady v USD bez DPH; skutečnou útratu ukáže vyúčtování poskytovatele.</p>
        </div>
      </div>

      <details className={box}>
        <summary className={`${label} cursor-pointer`}>Po dnech (30 dní)</summary>
        <div className="max-h-64 overflow-auto mt-2">
          <table className="w-full text-[11px]"><thead><tr className="text-left text-gray-500"><th>Den</th><th>Zákazníci</th><th>Požadavky</th><th>Obrázky</th><th>Kč</th>{activeKeys.map((k: string) => <th key={k}>{usedNames[k] || k}</th>)}</tr></thead><tbody>
            {[...d.daily].reverse().map((x: any) => (
              <tr key={x.day} className="border-t border-gray-200"><td className="py-1 whitespace-nowrap">{x.day.split('-').reverse().join('. ')}</td><td>{x.users}</td><td>{x.requests}</td><td>{x.images}</td><td>{Math.round(x.costUsd * rate)}</td>{activeKeys.map((k: string) => <td key={k}>{x.models[k] || 0}</td>)}</tr>
            ))}
          </tbody></table>
        </div>
      </details>

      <div className={`${box} flex items-center justify-between gap-4 flex-wrap`}>
        <div><span className={label}>Kurz pro přepočet nákladů AI (Kč za 1 USD)</span><span className={hint}>Použije se ve všech přehledech nákladů.</span></div>
        <input type="number" min={1} step={0.1} value={g.czkPerUsd ?? 23} onChange={e => save({ czkPerUsd: Math.max(1, parseFloat(e.target.value) || 23) })} className={num} />
      </div>
    </div>
  );
};

// ---------------- Uživatelé ----------------
const ROLE_NAMES: Record<string, string> = { superadministrator: 'Superadministrátor', administrator: 'Administrátor', user: 'Uživatel' };
const shortUa = (ua?: string | null) => {
  if (!ua) return '';
  const b = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  const o = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return [b, o].filter(Boolean).join(' · ');
};
const UsersSection: React.FC<{ isSuper: boolean; email?: string }> = ({ isSuper, email }) => {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = () => adminFetch('/api/admin/users-overview').then(r => { setD(r); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { if (isSuper) load(); }, [isSuper]);
  if (!isSuper) return <Note m="Přehled uživatelů a jejich přihlášení vidí jen superadministrátor." />;
  if (err) return <Err m={err} />;
  if (!d) return <div className={box}><span className={hint}>Načítám uživatele…</span></div>;
  const revoke = async (u: any) => {
    if (!window.confirm(`Odhlásit ${u.email} ze všech zařízení? Při dalším přihlášení zadá heslo i kód 2FA znovu.`)) return;
    try { await adminFetch('/api/admin/users/revoke', { body: { uid: u.uid } }); setMsg(`${u.email} odhlášen ze všech zařízení.`); load(); }
    catch (e: any) { setMsg(e.message); }
  };
  const s = d.summary;
  return (
    <div className="space-y-3">
      <h5 className="rda-h5">Uživatelé administrace</h5>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat t="Uživatelů celkem" v={s.total} s={`${s.superadmins} superadmin · ${s.admins} admin`} />
        <Stat t="Aktivní za 7 dní" v={s.active7} s="podle poslední aktivity" />
        <Stat t="S dvoufázovým ověřením" v={`${s.withTwofa} / ${s.total}`} s={s.withTwofa < s.total ? 'někdo ho ještě nemá' : 'všichni'} />
        <Stat t="Nikdy nepřihlášen" v={s.neverSigned} s="pozvánka bez přihlášení" />
      </div>
      {msg && <Note m={msg} />}
      <div className={`${box} overflow-x-auto`}>
        <table className="w-full text-[11px] min-w-[760px]">
          <thead><tr className="text-left text-gray-500"><th className="py-1">Účet</th><th>Role</th><th>Přihlášení</th><th>Poslední přihlášení</th><th>Poslední aktivita</th><th>30 dní</th><th>2FA</th><th></th></tr></thead>
          <tbody>
            {d.users.map((u: any) => (
              <tr key={u.email} className="border-t border-gray-200 align-top">
                <td className="py-1.5 pr-2"><span className="font-bold block">{u.email}{u.email === email ? ' (vy)' : ''}</span>{u.name && <span className="text-gray-500">{u.name}</span>}
                  {!u.exists && <span className="block text-amber-700 font-bold">účet zatím nevznikl</span>}
                  {u.disabled && <span className="block text-red-700 font-bold">účet zakázán</span>}
                  {u.locked && <span className="block text-red-700 font-bold">dočasně zablokován (chybné kódy)</span>}
                </td>
                <td>{ROLE_NAMES[u.role] || u.role}</td>
                <td>{u.providers.join(', ') || '—'}</td>
                <td className="whitespace-nowrap">{fmtDate(u.lastSignIn)}<span className="block text-gray-500">{ago(u.lastSignIn)}{u.lastLogin ? ` · ${u.lastLogin.method === 'google' ? 'Google' : 'heslo'}` : ''}</span>
                  {u.lastLogin && <span className="block text-gray-400 font-mono">{u.lastLogin.ip} {shortUa(u.lastLogin.ua) && <span className="font-sans">· {shortUa(u.lastLogin.ua)}</span>}</span>}
                </td>
                <td className="whitespace-nowrap">{fmtDate(u.lastActive)}<span className="block text-gray-500">{ago(u.lastActive)}</span></td>
                <td>{u.logins30}× OK{u.fails30 ? <span className="block text-red-700 font-bold">{u.fails30}× neúspěšně</span> : null}</td>
                <td className={u.twofa ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>{u.twofa ? `zapnuto · ${u.recoveryLeft} kódů` : 'nemá'}</td>
                <td className="text-right">{u.uid && u.email !== email && <button type="button" className="text-red-600 underline text-[10px] font-bold whitespace-nowrap" onClick={() => revoke(u)}>Odhlásit všude</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[9px] text-gray-400 mt-2">Poslední přihlášení a aktivita podle Firebase Authentication; IP, zařízení a počty podle záznamu přihlášení této aplikace (30 dní).</p>
      </div>
    </div>
  );
};

// ---------------- Zabezpečení: souhrn ----------------
const SecuritySummary: React.FC = () => {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { adminFetch('/api/admin/security-summary').then(setD).catch(e => setErr(e.message)); }, []);
  if (err) return <Err m={err} />;
  if (!d) return null;
  const Row: React.FC<{ ok: boolean; t: string; s: string }> = ({ ok, t, s }) => (
    <li className="flex gap-2"><span className={ok ? 'text-green-700 font-black' : 'text-amber-700 font-black'}>{ok ? 'OK' : 'POZOR'}</span><span className="font-bold">{t}</span><span className="text-gray-500">{s}</span></li>
  );
  return (
    <div className="space-y-3">
      <h5 className="rda-h5">Přehled zabezpečení</h5>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat t="Přihlášení 24 h" v={d.ok24} s={`${d.ok7} za 7 dní`} />
        <Stat t="Neúspěšná 24 h" v={d.fail24} s={`${d.fail7} za 7 dní`} />
        <Stat t="Zablokované účty" v={d.lockedNow} s="po chybných kódech 2FA" />
        <Stat t="Nejvíc chyb z IP" v={d.topFailIps[0]?.n || 0} s={d.topFailIps[0]?.ip || 'žádné'} />
      </div>
      <div className={box}>
        <ul className="space-y-1 text-[11px]">
          <Row ok={d.twofaForced} t="Dvoufázové ověření pro administrátory" s={d.twofaForced ? 'povinné' : 'vypnuto proměnnou ADMIN_2FA_DISABLE'} />
          <Row ok={d.alertWebhook} t="Bezpečnostní upozornění" s={d.alertWebhook ? 'adresa nastavená' : 'není kam posílat – doplňte webhook níže'} />
          <Row ok={true} t="Ověření požadavků (App Check / reCAPTCHA)" s="AI, PDF a nahrávání přijímá jen z editoru" />
          <Row ok={true} t="Klíče AI" s="uložené šifrovaně jen na serveru, do prohlížeče se neposílají" />
        </ul>
      </div>
    </div>
  );
};

// ---------------- Košík ----------------
const CartSection: React.FC = () => {
  const [g, save] = useGeneral();
  const bands: PriceBand[] = Array.isArray(g.priceTable) ? g.priceTable : [];
  const setBands = (b: PriceBand[]) => save({ priceTable: b });
  return (
    <div className="space-y-4">
      <h4 className={h4}>Kontrola před košíkem</h4>
      <Toggle title="Upozornit před vložením do košíku" desc="Ukázkový nebo chybějící text, prázdný arch a obrázky s nízkým rozlišením. Zákazník může vždy pokračovat."
        checked={g.preCartCheck !== false} onChange={v => save({ preCartCheck: v })} />
      <div className={`${box} flex items-center justify-between gap-4 flex-wrap`}>
        <div><span className={label}>Minimální rozlišení obrázku (DPI)</span><span className={hint}>Pod touto hodnotou se zobrazí upozornění na neostrý tisk.</span></div>
        <input type="number" min={50} max={600} value={g.minPrintDpi ?? 150} onChange={e => save({ minPrintDpi: Math.max(50, parseInt(e.target.value, 10) || 150) })} className={num} />
      </div>

      <h4 className={h4}>Orientační cena v editoru</h4>
      <p className="text-[10px] text-gray-500">Cena za arch podle počtu archů v objednávce. Když je ceník prázdný, editor dál píše „Cenu uvidíte v košíku e-shopu“. Ceny zadejte tak, jak je chcete zákazníkovi ukázat (např. s DPH).</p>
      <div className="space-y-2">
        {bands.map((b, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black text-gray-500 uppercase">od</span>
            <input type="number" min={1} value={b.fromSheets} onChange={e => setBands(bands.map((x, j) => j === i ? { ...x, fromSheets: Math.max(1, parseInt(e.target.value, 10) || 1) } : x))} className={num} />
            <span className="text-[10px] font-black text-gray-500 uppercase">archů</span>
            <input type="number" min={0} step={0.01} value={b.pricePerSheet} onChange={e => setBands(bands.map((x, j) => j === i ? { ...x, pricePerSheet: Math.max(0, parseFloat(e.target.value) || 0) } : x))} className={num} />
            <span className="text-[10px] font-black text-gray-500 uppercase">{g.priceCurrency || 'Kč'} / arch</span>
            <button type="button" className={btn} onClick={() => setBands(bands.filter((_, j) => j !== i))}>Odebrat</button>
          </div>
        ))}
        <button type="button" className={btn} onClick={() => setBands([...bands, { fromSheets: bands.length ? Math.max(...bands.map(b => b.fromSheets)) * 2 : 1, pricePerSheet: bands[bands.length - 1]?.pricePerSheet || 0 }])}>Přidat pásmo</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div><span className={label}>Měna</span><input className={txt} value={g.priceCurrency ?? 'Kč'} onChange={e => save({ priceCurrency: e.target.value.slice(0, 8) })} /></div>
        <div className="sm:col-span-2"><span className={label}>Poznámka k ceně</span><input className={txt} value={g.priceNote ?? ''} placeholder="např. vč. DPH, bez dopravy" onChange={e => save({ priceNote: e.target.value.slice(0, 60) })} /></div>
      </div>
    </div>
  );
};

// ---------------- Zabezpečení ----------------
const SecuritySection: React.FC<{ isSuper: boolean; email?: string }> = ({ isSuper, email }) => {
  const [g, save] = useGeneral();
  const [me, setMe] = useState<any>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [list, setList] = useState<any[] | null>(null);
  const [logins, setLogins] = useState<any[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    adminFetch('/api/admin/whoami').then(setMe).catch(e => setMsg(e.message));
    if (isSuper) {
      adminFetch<{ items: any[] }>('/api/admin/2fa/list').then(r => setList(r.items)).catch(() => {});
      adminFetch<{ items: any[] }>('/api/admin/logins').then(r => setLogins(r.items)).catch(() => {});
    }
  };
  useEffect(load, [isSuper]);

  const disable = async (uid?: string) => {
    if (!window.confirm(uid ? 'Vypnout dvoufázové ověření tomuto účtu (např. ztracený telefon)?' : 'Opravdu vypnout dvoufázové ověření pro váš účet?')) return;
    try { await adminFetch('/api/admin/2fa/disable', { body: uid ? { uid } : {} }); if (!uid) adminSession.clear(true); load(); await authService.recheck(); }
    catch (e: any) { setMsg(e.message); }
  };
  const revoke = async () => {
    try { await adminFetch('/api/admin/2fa/revoke-devices', { body: {} }); adminSession.clear(true); setMsg('Všechna zařízení odhlášena. Při další akci zadáte kód znovu.'); await authService.recheck(); }
    catch (e: any) { setMsg(e.message); }
  };

  const t = me?.twofa;
  return (
    <div className="space-y-3">
      <h5 className="rda-h5">Zabezpečení přihlášení</h5>
      {msg && <div className="p-2 text-xs font-bold bg-amber-50 text-amber-800 border-2 border-amber-100 rounded-md">{msg}</div>}
      <div className={`${box} space-y-3`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className={label}>Dvoufázové ověření vašeho účtu</span>
            <span className={hint}>{t ? (t.enabled ? `Zapnuto · zbývá ${t.recoveryLeft} záložních kódů` : 'Vypnuto – bez něj se administrace neodemkne') : 'Načítám…'}</span>
          </div>
          {t && (t.enabled
            ? <div className="flex gap-2 flex-wrap"><button className={btn} onClick={revoke}>Odhlásit všechna zařízení</button><button className={btn} onClick={() => setSetupOpen(true)}>Nový telefon</button></div>
            : <button className={btnRed} onClick={() => setSetupOpen(true)}>Zapnout</button>)}
        </div>
      </div>
      {isSuper && (
        <>
          <div className={box}>
            <span className={label}>Dvoufázové ověření je pro administrátory povinné</span>
            <span className={hint}>Administrátorská práva (včetně zápisu do databáze) server přidělí až po zadání kódu, a to jen pro dané přihlášení na 12 hodin. Kdo ověření ještě nemá, založí si ho hned po přihlášení.</span>
          </div>
          <div className={box}>
            <span className={label}>Adresa pro bezpečnostní upozornění (webhook)</span>
            <span className={hint}>Opakovaná chybná přihlášení, blokace účtu, použití záložního kódu. Když je prázdná, použije se adresa pro hlášení chyb.</span>
            <input className={`${txt} mt-2 font-mono`} value={g.securityWebhookUrl ?? ''} placeholder="https://hook.eu1.make.com/…" onChange={e => save({ securityWebhookUrl: e.target.value.trim() })} />
          </div>
          {list && list.length > 0 && (
            <div className={box}>
              <span className={label}>Administrátoři s dvoufázovým ověřením</span>
              <table className="w-full text-xs mt-2"><tbody>
                {list.map(u => (
                  <tr key={u.uid} className="border-t border-gray-200">
                    <td className="py-1.5 font-bold">{u.email || u.uid}</td>
                    <td>{u.enabled ? `zapnuto (${u.recoveryLeft} kódů)` : 'vypnuto'}</td>
                    <td className="text-right">{u.enabled && u.email !== email && <button className="text-red-600 underline text-[10px] font-bold" onClick={() => disable(u.uid)}>Vypnout</button>}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
          {logins && (
            <details className={box}>
              <summary className={`${label} cursor-pointer`}>Poslední přihlášení ({logins.length})</summary>
              <div className="max-h-64 overflow-y-auto mt-2">
                <table className="w-full text-[11px]"><thead><tr className="text-left text-gray-500"><th>Kdy</th><th>Účet</th><th>Způsob</th><th>Výsledek</th><th>IP</th></tr></thead><tbody>
                  {logins.map((l, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="py-1 whitespace-nowrap">{l.at ? new Date(l.at).toLocaleString('cs-CZ') : ''}</td>
                      <td>{l.email || '—'}</td>
                      <td>{({ password: 'heslo', google: 'Google', totp: 'kód 2FA', recovery: 'záložní kód' } as any)[l.method] || l.method}{l.note ? ` · ${l.note}` : ''}</td>
                      <td className={l.ok ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>{l.ok ? 'OK' : (l.reason || 'chyba')}</td>
                      <td className="font-mono">{l.ip}</td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </details>
          )}
        </>
      )}
      {setupOpen && <TwoFactorModal mode="setup" optional email={email} onDone={() => { setSetupOpen(false); load(); }} onCancel={() => { setSetupOpen(false); load(); }} />}
    </div>
  );
};

// ---------------- Návštěvnost ----------------
const SOURCE_NAMES: Record<string, string> = { primo: 'Přímo / e-shop bez odkazu', google: 'Google', seznam: 'Seznam', meta: 'Facebook / Instagram' };
const LiveSection: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => adminFetch('/api/admin/live').then(d => { setData(d); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (err) return <div className="p-2 text-xs font-bold bg-red-50 text-red-700 border-2 border-red-100 rounded-md">{err}</div>;
  if (!data) return <div className={box}><span className={hint}>Načítám návštěvnost…</span></div>;
  const rate = data.czkPerUsd || 23;
  const today = data.days[data.days.length - 1] || {};
  const sum = (k: string) => data.days.reduce((n: number, d: any) => n + (d[k] || 0), 0);
  const sources: Record<string, number> = {};
  data.days.forEach((d: any) => Object.entries(d.sources || {}).forEach(([k, v]) => { sources[k] = (sources[k] || 0) + (v as number); }));
  const srcList = Object.entries(sources).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const srcName = (k: string) => SOURCE_NAMES[k] || (k.startsWith('utm:') ? `Kampaň: ${k.slice(4)}` : k.replace(/_/g, '.'));
  const kc = (usd: number) => `${Math.round(usd * rate).toLocaleString('cs-CZ')} Kč`;
  return (
    <div className="space-y-3">
      <h5 className="rda-h5">Klienti online</h5>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ['Za 2 minuty', data.windows?.[2] ?? 0, 'různých prohlížečů'],
          ['Za 5 minut', data.windows?.[5] ?? 0, ''],
          ['Za 15 minut', data.windows?.[15] ?? 0, ''],
          ['Za 30 minut', data.windows?.[30] ?? 0, ''],
        ].map(([t, v, s]) => (
          <div key={t as string} className={`${box} text-center`}>
            <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest block">{t}</span>
            <span className="text-2xl font-black text-gray-900 block">{v}</span>
            <span className="text-[10px] text-gray-500">{s}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-500">Obnovuje se každých 30 s. Administrátoři se nepočítají{data.adminsOnline ? ` (teď online: ${data.adminsOnline})` : ''}.</p>
      <h5 className="rda-h5">Dnes</h5>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ['Právě v editoru', data.active, `${data.interacting} upravuje${data.avgMinutes ? ` · prům. ${data.avgMinutes} min` : ''}`],
          ['Dnes otevřeno', today.opens || 0, `${today.interactions || 0} začalo upravovat`],
          ['Dnes do košíku', today.orders || 0, today.opens ? `${Math.round((today.orders || 0) / today.opens * 100)} % z otevření` : ''],
          ['AI dnes', today.aiImages || 0, `obrázků · ${kc(today.aiCostUsd || 0)}`],
        ].map(([t, v, s]) => (
          <div key={t as string} className={`${box} text-center`}>
            <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest block">{t}</span>
            <span className="text-2xl font-black text-gray-900 block">{v}</span>
            <span className="text-[10px] text-gray-500">{s}</span>
          </div>
        ))}
      </div>
      <div className={box}>
        <span className={label}>Posledních 30 dní</span>
        <span className={hint}>Otevřeno {sum('opens')} · upravovalo {sum('interactions')} · do košíku {sum('orders')} · AI {sum('aiImages')} obrázků za {kc(sum('aiCostUsd'))}</span>
        <div className="max-h-56 overflow-y-auto mt-2">
          <table className="w-full text-[11px]"><thead><tr className="text-left text-gray-500"><th>Den</th><th>Otevřeno</th><th>Upravovalo</th><th>Košík</th><th>AI obr.</th><th>AI Kč</th></tr></thead><tbody>
            {[...data.days].reverse().map((d: any) => (
              <tr key={d.day} className="border-t border-gray-200"><td className="py-1">{d.day.split('-').reverse().join('. ')}</td><td>{d.opens}</td><td>{d.interactions}</td><td>{d.orders}</td><td>{d.aiImages}</td><td>{Math.round(d.aiCostUsd * rate)}</td></tr>
            ))}
          </tbody></table>
        </div>
      </div>
      {srcList.length > 0 && (
        <div className={box}>
          <span className={label}>Odkud zákazníci přišli (30 dní)</span>
          <div className="mt-2 space-y-1">
            {srcList.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-[11px]">
                <span className="w-48 truncate font-bold">{srcName(k)}</span>
                <div className="flex-1 h-2 bg-gray-200 rounded"><div className="h-2 bg-red-600 rounded" style={{ width: `${Math.round(v / srcList[0][1] * 100)}%` }} /></div>
                <span className="w-10 text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[9px] text-gray-400">Počítá se jen v této instanci editoru, bez cookies a bez osobních údajů. Údaje se sbírají od nasazení této verze.</p>
    </div>
  );
};

// ---------------- Provoz ----------------
const HealthSection: React.FC = () => {
  const [r, setR] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const run = () => { setBusy(true); adminFetch('/api/admin/deploy-check').then(setR).catch(e => setR({ ok: false, checks: [{ name: 'Kontrola', ok: false, detail: e.message }] })).finally(() => setBusy(false)); };
  return (
    <div className="space-y-3">
      <h5 className="rda-h5">Provozní kontrola</h5>
      <div className={box}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><span className={label}>Stav serveru a napojení</span><span className={hint}>Databáze, složka objednávek na Disku, AI, převod obrázků, tiskové PDF.</span></div>
          <button className={btn} onClick={run} disabled={busy}>{busy ? 'Kontroluji…' : 'Zkontrolovat'}</button>
        </div>
        {r && (
          <ul className="mt-3 space-y-1 text-[11px]">
            {r.checks.map((c: any) => (
              <li key={c.name} className="flex gap-2"><span className={c.ok ? 'text-green-700 font-black' : 'text-red-700 font-black'}>{c.ok ? 'OK' : 'CHYBA'}</span><span className="font-bold">{c.name}</span><span className="text-gray-500 truncate">{c.detail}</span></li>
            ))}
            {r.version && <li className="text-gray-400 pt-1">Verze {String(r.version).slice(0, 7)} · instance {r.instance} · spuštěno {r.startedAt ? new Date(r.startedAt).toLocaleString('cs-CZ') : ''}</li>}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------- Tisk a barvy (jako v aplikaci pro diáře, navíc ořezová kontura) ----------------
const sel = 'w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-xs font-bold outline-none';
const INTENTS: [string, string][] = [
  ['relative', 'Relativní kolorimetrický + BPC'],
  ['perceptual', 'Perceptuální + BPC'],
  ['saturation', 'Sytost + BPC'],
  ['absolute', 'Absolutní kolorimetrický'],
];
const PrintColorSection: React.FC = () => {
  const [g, save] = useGeneral();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ printCmyk: boolean; printIccProfile: string; printIntent: string; cutContourName: string } | null>(null);
  const load = () => adminFetch('/api/admin/print-color').then(d => { setInfo(d); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (draft || !info) return;
    setDraft({
      printCmyk: g.printCmyk !== false,
      printIccProfile: g.printIccProfile === 'custom' && info.custom ? 'custom' : 'builtin-swop',
      printIntent: g.printIntent || 'relative',
      cutContourName: g.cutContourName || 'CutContour',
    });
  }, [g, info, draft]);
  const d = draft;
  const upload = async (f?: File | null) => {
    if (!f) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await adminUpload('/api/admin/print-icc', f);
      setMsg(`Profil ${r.name} nahrán a vybrán.`);
      setDraft(prev => prev ? { ...prev, printIccProfile: 'custom' } : prev);
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const removeCustom = async () => {
    if (!confirm('Smazat nahraný ICC profil? Tiskové PDF pak použije vestavěný profil.')) return;
    setBusy(true);
    try { await adminFetch('/api/admin/print-icc/delete', { method: 'POST', body: {} }); setDraft(p => p ? { ...p, printIccProfile: 'builtin-swop' } : p); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!d) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const cut = d.cutContourName.replace(/[^A-Za-z0-9_\-]/g, '').slice(0, 40) || 'CutContour';
      await save({ printCmyk: d.printCmyk, printIccProfile: d.printIccProfile, printIntent: d.printIntent, cutContourName: cut });
      setDraft({ ...d, cutContourName: cut });
      await adminFetch('/api/admin/print-color/reload', { method: 'POST', body: {} }).catch(() => {});
      setMsg('Nastavení uloženo. Platí pro další vytvořená tisková PDF.');
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <h4 className={h4}>Tisk a barvy</h4>
      {err && <Err m={err} />}
      {info && info.builtin && !info.builtin.available && <Note m="Na serveru chybí vestavěný CMYK profil – tisková PDF zůstanou v RGB s příponou _RGB." />}
      {!d ? <div className="text-xs text-gray-400">Načítám…</div> : (<>
        <Toggle title="Tiskové PDF v CMYK" desc="Převod přes ICC profil. Čistě bílá = 0/0/0/0, čistě černý text a čáry = jen K 100." checked={d.printCmyk} onChange={v => setDraft({ ...d, printCmyk: v })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={box}>
            <span className={label}>ICC profil CMYK</span>
            <select className={`${sel} mt-2`} value={d.printIccProfile} disabled={!d.printCmyk} onChange={e => setDraft({ ...d, printIccProfile: e.target.value })}>
              <option value="builtin-swop">{info?.builtin?.name || 'Vestavěný CMYK (Artifex SWOP)'}</option>
              {info?.custom && <option value="custom">Nahraný: {info.custom.name}</option>}
            </select>
          </div>
          <div className={box}>
            <span className={label}>Nahrát ICC profil (.icc)</span>
            <input type="file" accept=".icc,.icm" disabled={busy} className="mt-2 text-xs" onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            {info?.custom && <span className={hint}>Nahráno: {info.custom.name} · {Math.round(info.custom.size / 1024)} kB · {fmtDate(info.custom.uploadedAt)} <button type="button" className="underline text-red-600 ml-1" onClick={removeCustom}>smazat</button></span>}
          </div>
          <div className={box}>
            <span className={label}>Převodní záměr</span>
            <select className={`${sel} mt-2`} value={d.printIntent} disabled={!d.printCmyk} onChange={e => setDraft({ ...d, printIntent: e.target.value })}>
              {INTENTS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
            {d.printIntent === 'absolute' && <span className={hint}>Absolutní záměr napodobuje barvu papíru – bílá místa pak mohou dostat nádech. Pro samolepky nedoporučeno.</span>}
          </div>
          <div className={box}>
            <span className={label}>Název přímé barvy kontury</span>
            <input className={`${txt} mt-2`} value={d.cutContourName} maxLength={40} onChange={e => setDraft({ ...d, cutContourName: e.target.value })} />
            <span className={hint}>Ořezová cesta je v PDF vždy jako přímá barva (Separation), Caldera ji čte jako řez a netiskne.</span>
          </div>
        </div>
        <p className="text-[11px] text-gray-500">PDF je jen v CMYK + přímá barva ořezu, bílá se do něj nevkládá. Čistě bílá místa (i mezi samolepkami) zůstanou bez barvy. V Calderě nastavte stejný CMYK profil jako vstupní.</p>
        <div className="flex items-center gap-3">
          <button type="button" className="px-4 py-2 bg-[#1f5c63] text-white rounded-md text-[11px] font-black uppercase tracking-widest disabled:opacity-50" disabled={busy} onClick={submit}>Uložit nastavení</button>
          {msg && <span className="text-xs font-bold text-green-700">{msg}</span>}
          {info?.ghostscript && <span className="text-[10px] text-gray-400">Ghostscript {info.ghostscript}</span>}
        </div>
      </>)}
    </div>
  );
};

const AdminExtras: React.FC<Props> = ({ section, isSuper, email }) => {
  if (section === 'ai') return <AiSection isSuper={isSuper} />;
  if (section === 'cart') return <CartSection />;
  if (section === 'print') return <PrintColorSection />;
  if (section === 'online') return <div className="space-y-6"><LiveSection /></div>;
  if (section === 'users') return <div className="space-y-6"><UsersSection isSuper={isSuper} email={email} /></div>;
  if (section === 'security') return <div className="space-y-6"><SecuritySummary /><SecuritySection isSuper={isSuper} email={email} /></div>;
  if (section === 'health') return <div className="space-y-6"><HealthSection /></div>;
  return (
    <div className="space-y-6 mt-6">
      <LiveSection />
      <SecuritySection isSuper={isSuper} email={email} />
      <HealthSection />
    </div>
  );
};

export default AdminExtras;
